import { Gender, Prefecture, UrgentRelationship } from '@gyym/sdk'
import {
  Button,
  ButtonRadioField,
  cn,
  Field,
  Form,
  Grid,
  SearchableSelectField,
  SelectField,
  TextField,
  Typography,
} from '@gyym/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import React from 'react'
import { useController, useForm } from 'react-hook-form'
import { z, ZodIssueCode } from 'zod'

import { useAddress } from '@/modules/PostalCode'
import { prefectureOptions } from '@/modules/Prefectures'
import { nationalityLabels, nationalityOptions } from '@/modules/Users'

const genderOptions = [
  {
    label: '女性',
    value: Gender.WOMEN,
  },
  {
    label: '男性',
    value: Gender.MEN,
  },
]

const urgentRelationships: UrgentRelationship[] = [
  UrgentRelationship.SPOUSE,
  UrgentRelationship.FAMILY,
  UrgentRelationship.WORKPLACE,
  UrgentRelationship.OTHER,
]

const urgentRelationshipOptions = urgentRelationships.map((v) => ({
  label: v,
  value: v,
}))

const useBirthdayOptions = (year?: number, month?: number) => {
  const yearOptions = React.useMemo(() => {
    const currentYear = dayjs().year()
    return Array.from({ length: 110 }, (_, i) => ({
      label: `${currentYear - i}`,
      value: currentYear - i,
    }))
  }, [])

  const monthOptions = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        label: `${i + 1}`.padStart(2, '0'),
        value: i + 1,
      })),
    [],
  )

  const dayOptions = React.useMemo(() => {
    if (!year || !month) {
      return []
    }

    const daysInMonth = dayjs(`${year}-${month}`).daysInMonth()
    return Array.from({ length: daysInMonth }, (_, i) => ({
      label: `${i + 1}`.padStart(2, '0'),
      value: i + 1,
    }))
  }, [year, month])

  return { yearOptions, monthOptions, dayOptions }
}

const alphabetKatakanaRegex = /^([ァ-ヴa-zA-Z][ァ-ヴa-zA-Zー・]*)$/
const postalCodeRegex = /^[0-9]{3}-?[0-9]{4}$/

const validateTel = (value: string) => {
  if (/^(050|060|070|080|090)/.test(value)) {
    return /^[0-9]{11}$/.test(value)
  }
  return /^[0-9]{10,15}$/.test(value)
}

const urgentRelationshipSchema = z.discriminatedUnion(
  'urgentRelationship',
  [
    z.object({
      urgentRelationship: z.literal(UrgentRelationship.SPOUSE),
      urgentRelationshipOther: z
        .string()
        .optional()
        // Set to empty string
        .transform(() => ''),
    }),
    z.object({
      urgentRelationship: z.literal(UrgentRelationship.FAMILY),
      urgentRelationshipOther: z
        .string()
        .optional()
        // Set to empty string
        .transform(() => ''),
    }),
    z.object({
      urgentRelationship: z.literal(UrgentRelationship.WORKPLACE),
      urgentRelationshipOther: z
        .string()
        .optional()
        // Set to empty string
        .transform(() => ''),
    }),
    z.object({
      urgentRelationship: z.literal(UrgentRelationship.OTHER),
      urgentRelationshipOther: z.string().min(1, {
        message: '緊急連絡先の関係を入力してください',
      }),
    }),
  ],
  {
    errorMap: (issue, ctx) => {
      if (issue.code === ZodIssueCode.invalid_union_discriminator) {
        return {
          message: '緊急連絡先の属性を選択してください',
        }
      }
      return { message: ctx.defaultError }
    },
  },
)

export const baseSchema = z
  .object({
    lastName: z
      .string()
      // Disable alphanumeric characters
      .regex(/^[^0-9]*$/, {
        message: '名に不正な文字が含まれています',
      })
      .min(1, {
        message: '姓を入力してください',
      }),
    firstName: z
      .string()
      // Disable alphanumeric characters
      .regex(/^[^0-9]*$/, {
        message: '名に不正な文字が含まれています',
      })
      .min(1, {
        message: '名を入力してください',
      }),
    lastNameKana: z
      .string()
      .min(1, {
        message: '姓(カナ)を入力してください',
      })
      .regex(alphabetKatakanaRegex, {
        message: '全角カタカナで入力してください',
      }),
    firstNameKana: z
      .string()
      .min(1, {
        message: '名(カナ)を入力してください',
      })
      .regex(alphabetKatakanaRegex, {
        message: '全角カタカナで入力してください',
      }),
    nationality: z
      .string()
      .min(1, { message: '国籍を選択してください' })
      .refine((v) => v in nationalityLabels, {
        message: '国籍を選択してください',
      }),
    sex: z.nativeEnum(Gender).default(Gender.WOMEN),
    birthday: z.string().min(1, {
      message: '生年月日を入力してください',
    }),
    tel: z
      .string()
      .min(1, {
        message: '電話番号を入力してください',
      })
      .refine(validateTel, {
        message: '電話番号は正しい形式で入力してください',
      }),
    urgentTel: z
      .string()
      .min(1, {
        message: '緊急連絡先の電話番号を入力してください',
      })
      .refine(validateTel, {
        message: '電話番号は正しい形式で入力してください',
      }),
    postalCode: z
      .string()
      .regex(postalCodeRegex, {
        message: '郵便番号が不正です',
      })
      .min(1, {
        message: '郵便番号を入力してください',
      })
      .max(8, {
        message: '郵便番号が不正です',
      })
      // Remove optional hyphen
      .transform((value) => value.replace('-', '')),
    prefecture: z.nativeEnum(Prefecture, {
      required_error: '都道府県を選択してください',
    }),
    address1: z.string().min(1, {
      message: '住所を入力してください',
    }),
    address2: z.string().optional(),
  })
  // Validate urgentTel and tel are not same
  .refine(({ tel, urgentTel }) => tel !== urgentTel, {
    path: ['urgentTel'],
    message: 'ご本人以外の連絡先をご登録ください',
  })

const schema = baseSchema.and(urgentRelationshipSchema)

export type FormValues = z.infer<typeof schema>

const initialValues: Partial<FormValues> = {
  lastName: '',
  firstName: '',
  lastNameKana: '',
  firstNameKana: '',
  nationality: '',
  sex: Gender.WOMEN,
  birthday: '',
  tel: '',
  urgentTel: '',
  postalCode: '',
  address1: '',
  address2: '',
}

export interface ProfileFormProps {
  /**
   * Initial form values
   */
  values?: Partial<FormValues>

  /**
   * Submit handler
   */
  onSubmit: (values: FormValues) => void

  /**
   * Children render prop
   */
  children?: ({
    fields,
    submit,
  }: {
    fields: React.ReactNode
    submit: React.ReactNode
  }) => React.ReactNode
}

export const ProfileForm = ({
  values,
  onSubmit,
  children,
}: ProfileFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormValues>({
    // Validation will trigger on the first blur event.
    // After the first blur event, validation will trigger on every change event.
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: {
      ...initialValues,
      ...values,
    },
  })

  const [isConfirming, setConfirming] = React.useState(false)

  const onSubmitWrapper = React.useCallback<typeof onSubmit>(
    async (...args) => {
      try {
        await onSubmit(...args)
      } catch (_e) {
        const e = _e as Record<string, Record<string, string[]>>
        setConfirming(false)
        if (e.errors && typeof e.errors === 'object') {
          Object.keys(e.errors).forEach((key) => {
            setError(key as keyof FormValues, {
              type: 'custom',
              message: e.errors?.[key]?.[0],
            })
          })
        }
      }
    },
    [setError, onSubmit],
  )

  // Handle birthday field
  register('birthday')
  const [birthdayValue, setBirthday] = React.useState<{
    year?: number
    month?: number
    day?: number
  }>({
    year: undefined,
    month: undefined,
    day: undefined,
  })
  React.useEffect(() => {
    const { year, month, day } = birthdayValue

    if (year && month && day) {
      setValue(
        'birthday',
        `${year}-${`${month}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`,
        {
          shouldValidate: true,
        },
      )
    }
  }, [birthdayValue, setValue])

  const { field: nationalityField } = useController({
    control,
    name: 'nationality',
    defaultValue: '',
  })

  const { field: sexField } = useController({
    control,
    name: 'sex',
    defaultValue: Gender.WOMEN,
  })

  const { field: urgentRelationshipField } = useController({
    control,
    name: 'urgentRelationship',
    defaultValue: undefined,
  })

  const { field: prefectureField } = useController({
    control,
    name: 'prefecture',
    defaultValue: undefined,
  })

  const { yearOptions, monthOptions, dayOptions } = useBirthdayOptions(
    birthdayValue.year,
    birthdayValue.month,
  )

  const postalCodeValue = watch('postalCode')

  const { prefecture, address } = useAddress(postalCodeValue)

  React.useEffect(() => {
    if (prefecture) {
      setValue('prefecture', prefecture.id, { shouldValidate: true })
    }
    if (address) {
      setValue('address1', address, { shouldValidate: true })
    }
  }, [prefecture, address, setValue])

  const fields = !isConfirming ? (
    <>
      <Field>
        <Field.Label>お名前</Field.Label>
        <Field.Content>
          <Grid columns={2} gap={2}>
            <TextField placeholder="姓" {...register('lastName')} />
            <TextField placeholder="名" {...register('firstName')} />
          </Grid>

          <Field.Messages
            mt={2}
            type="error"
            messages={
              [errors.lastName?.message, errors.firstName?.message].filter(
                (v) => v,
              ) as string[]
            }
          />

          <Grid mt={2} columns={2} gap={2}>
            <TextField placeholder="カナ(姓)" {...register('lastNameKana')} />
            <TextField placeholder="カナ(名)" {...register('firstNameKana')} />
          </Grid>

          <Field.Messages
            mt={2}
            type="error"
            messages={
              [
                errors.lastNameKana?.message,
                errors.firstNameKana?.message,
              ].filter((v) => v) as string[]
            }
          />
        </Field.Content>

        <Field.Messages
          mt={2}
          type="caption"
          messages={[
            '身分証明書と同じお名前をご記入ください。ニックネームのご利用はできません。',
          ]}
        />
      </Field>

      <SearchableSelectField
        mt={4}
        label="国籍"
        placeholder="国籍を選択"
        noMatchMessage="一致する国籍がありません"
        options={nationalityOptions}
        value={nationalityField.value}
        onChange={nationalityField.onChange}
        onBlur={nationalityField.onBlur}
        errorMessages={errors.nationality?.message}
      />

      <ButtonRadioField
        mt={4}
        label="性別"
        options={genderOptions}
        value={sexField.value}
        onChange={sexField.onChange}
        multiple={false}
      />

      <Field mt={4}>
        <Field.Label>生年月日</Field.Label>
        <Field.Content>
          <Grid
            gridTemplateColumns="1fr 8px 1fr 8px 1fr"
            gap={2}
            alignItems="center"
            justifyItems="center"
          >
            <SelectField
              placeholder="年"
              options={yearOptions}
              value={birthdayValue.year}
              onChange={(value) => {
                setBirthday((prev) => ({
                  ...prev,
                  year: value,
                  day: 1,
                }))
              }}
            />
            <Typography variant="subhead1" as="div" color="gray.02" mb={1}>
              /
            </Typography>
            <SelectField
              placeholder="月"
              options={monthOptions}
              value={birthdayValue.month}
              onChange={(value) => {
                setBirthday((prev) => ({
                  ...prev,
                  month: value,
                  day: 1,
                }))
              }}
            />
            <Typography variant="subhead1" as="div" color="gray.02" mb={1}>
              /
            </Typography>
            <SelectField
              placeholder="日"
              options={dayOptions}
              value={birthdayValue.day}
              onChange={(value) => {
                setBirthday((prev) => ({ ...prev, day: value }))
              }}
            />
          </Grid>
        </Field.Content>

        <Field.Messages
          mt={2}
          type="error"
          messages={errors.birthday?.message}
        />
      </Field>

      <TextField
        mt={6}
        label="電話番号"
        placeholder="080xxxxxxxx"
        {...register('tel')}
        errorMessages={errors.tel?.message}
      />

      <Field mt={4}>
        <Field.Label>緊急連絡先</Field.Label>
        <Field.Content>
          <Grid columns={1} gap={2}>
            <TextField
              mt={4}
              placeholder="080xxxxxxxx"
              {...register('urgentTel')}
              errorMessages={errors.urgentTel?.message}
            />
            <SelectField
              placeholder="属性を選択"
              options={urgentRelationshipOptions}
              {...urgentRelationshipField}
              errorMessages={errors.urgentRelationship?.message}
            />
            {watch('urgentRelationship') === UrgentRelationship.OTHER && (
              <TextField
                placeholder="その他の場合入力"
                {...register('urgentRelationshipOther')}
                errorMessages={errors.urgentRelationshipOther?.message}
              />
            )}
          </Grid>
        </Field.Content>

        <Field.Messages
          mt={2}
          type="caption"
          messages={['事故等の緊急時に連絡を取れる方の記載をお願いします。']}
        />
      </Field>

      <Field mt={6}>
        <Field.Label>住所</Field.Label>
        <Field.Content>
          <Grid
            gridTemplateColumns="12px 1fr"
            gap={2}
            alignItems="center"
            justifyItems="center"
          >
            <Typography variant="body1" as="div" color="gray.02" mb={0.5}>
              〒
            </Typography>
            <TextField placeholder="000-0000" {...register('postalCode')} />
          </Grid>

          <Field.Messages
            mt={2}
            type="error"
            messages={errors.postalCode?.message}
          />

          <Grid mt={4} columns={1} gap={2}>
            <SelectField
              placeholder="都道府県を選択"
              options={prefectureOptions}
              {...prefectureField}
              errorMessages={errors.prefecture?.message}
            />
            <TextField
              placeholder="住所を入力"
              {...register('address1')}
              errorMessages={errors.address1?.message}
            />
            <TextField
              placeholder="番地以降の住所を入力"
              {...register('address2')}
              errorMessages={errors.address2?.message}
            />
          </Grid>
        </Field.Content>
      </Field>
    </>
  ) : (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Typography variant="subhead2">お名前</Typography>
          <Typography variant="body1" mt={2}>
            {watch('lastName')} {watch('firstName')}
          </Typography>
          <Typography variant="body1" mt={2}>
            {watch('lastNameKana')} {watch('firstNameKana')}
          </Typography>
        </div>

        <div className="flex flex-col">
          <Typography variant="subhead2">国籍</Typography>
          <Typography variant="body1" mt={2}>
            {
              nationalityOptions.find((n) => n.value === watch('nationality'))
                ?.label
            }
          </Typography>
        </div>

        <div className="flex flex-col">
          <Typography variant="subhead2">性別</Typography>
          <Typography variant="body1" mt={2}>
            {genderOptions.find((g) => g.value === watch('sex'))!.label}
          </Typography>
        </div>

        <div className="flex flex-col">
          <Typography variant="subhead2">生年月日</Typography>
          <Typography variant="body1" mt={2}>
            {watch('birthday').replace(/-/g, '/')}
          </Typography>
        </div>

        <div className="flex flex-col">
          <Typography variant="subhead2">電話番号</Typography>
          <Typography variant="body1" mt={2}>
            {watch('tel')}
          </Typography>
        </div>

        <div className="flex flex-col">
          <Typography variant="subhead2">緊急連絡先</Typography>
          <Typography variant="body1" mt={2}>
            {watch('urgentTel')}
          </Typography>
          <Typography variant="body1" mt={2}>
            {
              urgentRelationshipOptions.find(
                (g) => g.value === watch('urgentRelationship'),
              )!.label
            }
          </Typography>
          {watch('urgentRelationshipOther') && (
            <Typography variant="body1" mt={2}>
              {watch('urgentRelationshipOther')}
            </Typography>
          )}
        </div>

        <div className="flex flex-col">
          <Typography variant="subhead2">住所</Typography>
          <Typography variant="body1" mt={2}>
            {postalCodeValue.includes('-')
              ? postalCodeValue
              : `${postalCodeValue.substring(0, 3)}-${postalCodeValue.substring(
                  3,
                )}`}
          </Typography>
          <Typography variant="body1" mt={2}>
            {
              prefectureOptions.find((g) => g.value === watch('prefecture'))!
                .label
            }
            {watch('address1')}
            {watch('address2')}
          </Typography>
        </div>

        <div className="flex items-center justify-center">
          <Button variant="primary-text" onClick={() => setConfirming(false)}>
            内容を修正
          </Button>
        </div>
      </div>
    </>
  )

  const hasChildren = !!children

  const submit = (
    <Button
      className={cn({
        'mt-6': !hasChildren,
      })}
      type={isConfirming ? 'submit' : 'button'}
      onClick={
        isConfirming
          ? undefined
          : (e) => {
              e.preventDefault()
              setConfirming(true)
            }
      }
      variant="brand"
      size="lg"
      fullWidth
      disabled={!isValid || isSubmitting}
    >
      {isConfirming ? 'この内容で登録' : '登録内容を確認'}
    </Button>
  )

  return (
    <Form onSubmit={handleSubmit(onSubmitWrapper)}>
      {hasChildren ? (
        children({ fields, submit })
      ) : (
        <>
          {fields}
          {submit}
        </>
      )}
    </Form>
  )
}
